import { useId } from "react";

import { RadioGroup, RadioGroupItem } from "@/components/ui/RadioGroup";
import { usePatchCart } from "@/customer/cart/hooks";
import type { Cart, PaymentMethodType } from "@/customer/cart/types";

interface MethodOption {
  value: PaymentMethodType;
  label: string;
  description: string;
  disabled?: boolean;
  disabledReason?: string;
}

const OPTIONS: MethodOption[] = [
  {
    value: "card",
    label: "Card",
    description: "Card payments online — coming soon. Choose ACH or Check, or call (303) 435-4328.",
    disabled: true,
    disabledReason: "card_coming_soon"
  },
  {
    value: "ach",
    label: "ACH bank transfer",
    description: "Pay from your bank account. We'll contact you to capture the routing + account number."
  },
  {
    value: "check",
    label: "Check",
    description: "Mail or drop off a check. Your reservation holds while we wait for payment."
  }
];

// Payment-method selector. Card is intentionally disabled in this slice
// with a transparent affordance — Stripe Elements ships in a follow-up.
// ACH/Check both land the order in `pending_payment` and JDR contacts
// the customer to finalize payment.
export function PaymentSection({
  cart,
  onAdvance
}: {
  cart: Cart;
  onAdvance(): void;
}): JSX.Element {
  const groupId = useId();
  const patch = usePatchCart();
  const value = cart.payload.payment_method_type;

  function handleChange(next: string): void {
    const cast = next as PaymentMethodType;
    patch.mutate(
      { payment_method_type: cast },
      { onSuccess: () => onAdvance() }
    );
  }

  return (
    <div className="space-y-4">
      <RadioGroup
        aria-labelledby={`${groupId}-label`}
        value={value}
        onValueChange={handleChange}
        className="space-y-2"
      >
        <p id={`${groupId}-label`} className="sr-only">
          Payment method
        </p>
        {OPTIONS.map((opt) => (
          <label
            key={opt.value}
            className="flex items-start gap-3 rounded-md border border-zinc-200 p-3 hover:border-zinc-300 has-[input:disabled]:cursor-not-allowed has-[input:disabled]:opacity-60"
          >
            <RadioGroupItem
              value={opt.value}
              disabled={opt.disabled}
              className="mt-0.5"
            />
            <span className="flex-1 text-sm">
              <span className="font-semibold text-brand-black">{opt.label}</span>
              <span className="ml-2 text-zinc-600">— {opt.description}</span>
            </span>
          </label>
        ))}
      </RadioGroup>

      <p className="text-xs text-zinc-500">
        For ACH or Check, your reservation is held in <strong>pending payment</strong>
        {" "}until JDR captures payment.
      </p>
    </div>
  );
}
